import { EllipsisIcon } from '@daily/shared/components/Icons/EllipsisIcon';
import { PinIcon } from '@daily/shared/components/Icons/PinIcon';
import { ScreenshareIcon } from '@daily/shared/components/Icons/ScreenshareIcon';
import { StopScreenshareIcon } from '@daily/shared/components/Icons/StopScreenshareIcon';
import { Menu } from '@daily/shared/components/Menu';
import { Stack } from '@daily/shared/components/Stack';
import { Tab } from '@daily/shared/components/Tabs';
import { Text } from '@daily/shared/components/Text';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import {
  ScreenShare,
  useDaily,
  useLocalSessionId,
  useParticipantProperty,
  useScreenShare,
} from '@daily-co/daily-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDisplayName } from '/hooks/useDisplayName';
import { useShowNames } from '/lib/state/custom';
import { usePinnedId } from '/lib/state/layout';

interface Props {
  index: number;
  screen: ScreenShare;
}

export const ScreenTab: React.FC<Props> = ({ index, screen }) => {
  const { t } = useTranslation();
  const daily = useDaily();
  const [showMenu, setShowMenu] = useState(false);
  const [pinnedId, setPinnedId] = usePinnedId();
  const { stopScreenShare } = useScreenShare();
  const localSessionId = useLocalSessionId();
  const [showNames] = useShowNames();

  const isOwner = useParticipantProperty(localSessionId, 'owner');

  const btnId = useId();
  const menuId = useId();

  const handleClickPin = () => {
    setPinnedId((p) => (p === screen.screenId ? null : screen.screenId));
    setShowMenu(false);
  };

  const handleClickStop = () => {
    setShowMenu(false);
    stopScreenShare();
  };

  const handleClickRemoteStop = () => {
    setShowMenu(false);
    daily.sendAppMessage('stop-screen-share', screen.session_id);
  };

  useEffect(() => {
    setShowMenu(false);
  }, [pinnedId]);

  let name = useDisplayName(screen.session_id, showNames);
  if (screen.session_id === localSessionId) {
    name = t('people.you');
  }

  return (
    <>
      <Tab
        index={index}
        icon={<ScreenshareIcon size={16} />}
        menuButton={{
          'aria-controls': menuId,
          id: btnId,
          children: (
            <>
              <EllipsisIcon size={16} />
              <VisuallyHidden>{t('screenShare.showOptions')}</VisuallyHidden>
            </>
          ),
          onClick: () => setShowMenu(true),
          open: showMenu,
        }}
      >
        <Stack align="center" gap={4} horizontal>
          <Text El="span" style={{ maxWidth: 200 }} truncate>
            {t('general.screenShare')} ({name})
          </Text>
          {pinnedId === screen.screenId && <PinIcon size={16} />}
        </Stack>
      </Tab>
      {showMenu && (
        <Menu
          id={menuId}
          align="left"
          aria-labelledby={btnId}
          offsetTop={0}
          placement="bottom"
          items={[
            {
              label:
                pinnedId === screen.screenId
                  ? t('screenShare.unpin')
                  : t('screenShare.pin'),
              icon: <PinIcon size={16} />,
              onClick: handleClickPin,
            },
            screen.session_id === localSessionId
              ? {
                  label: t('screenShare.stopSharing'),
                  icon: <StopScreenshareIcon size={16} />,
                  onClick: handleClickStop,
                }
              : null,
            isOwner && screen.session_id !== localSessionId
              ? {
                  label: t('screenShare.stopShare'),
                  icon: <StopScreenshareIcon size={16} />,
                  onClick: handleClickRemoteStop,
                }
              : null,
          ].filter(Boolean)}
          onClose={() => setShowMenu(false)}
        />
      )}
    </>
  );
};
